import {TrackUbaAudit} from './analytics';

const OFFLINE_QUEUE_KEY = 'error_toast_audit_offline_queue';
const MAX_OFFLINE_QUEUE_SIZE = 50;

// Fixed prefix on every entry — grep this string in server logs to find all error toasts
const LOG_TAG = 'ERROR_TOAST_AUDIT';

const generateErrorToastId = (): string => {
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `ET-${ts}-${rand}`;
};

const getOrCreateSessionId = (): string => {
  try {
    let id = sessionStorage.getItem('errorAuditSessionId');
    if (!id) {
      id = `SID-${Date.now()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
      sessionStorage.setItem('errorAuditSessionId', id);
    }
    return id;
  } catch {
    return 'unknown';
  }
};

interface ErrorToastLogEntry extends Record<string, unknown> {
  logTag: string;
  errorToastId: string;
  timestamp: string;
  toastErrorType: string;
  toastMessage: string;
  requestUrl: string;
  requestMethod: string;
  httpStatusCode: number;
  axiosErrorCode: string;
  responseErrorMessage: string;
  requestDurationMs: number | null;
  appVersion: string;
  sessionId: string;
  pageUrl: string;
  pagePath: string;
  sessionUsername: string;
  connectionType: string;
  isOnline: boolean;
}

const readOfflineQueue = (): ErrorToastLogEntry[] => {
  try {
    const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
    return raw ? (JSON.parse(raw) as ErrorToastLogEntry[]) : [];
  } catch {
    return [];
  }
};

const writeOfflineQueue = (queue: ErrorToastLogEntry[]): void => {
  try {
    localStorage.setItem(
      OFFLINE_QUEUE_KEY,
      JSON.stringify(queue.slice(-MAX_OFFLINE_QUEUE_SIZE))
    );
  } catch {
    // localStorage may be full or unavailable (private browsing, storage quota)
  }
};

const getConnectionType = (): string => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conn = (navigator as any).connection;
    return conn?.effectiveType ?? conn?.type ?? 'unknown';
  } catch {
    return 'unknown';
  }
};

const buildEntry = (
  toastErrorType: string,
  toastMessage: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any
): ErrorToastLogEntry => ({
  logTag: LOG_TAG,
  errorToastId: generateErrorToastId(),
  timestamp: new Date().toISOString(),
  toastErrorType,
  toastMessage,
  requestUrl: String(error?.config?.url ?? ''),
  requestMethod: String(error?.config?.method ?? '').toUpperCase(),
  httpStatusCode: Number(error?.response?.status ?? 0),
  axiosErrorCode: String(error?.code ?? ''),
  responseErrorMessage: String(
    error?.response?.data?.error?.message ??
      error?.response?.data?.message ??
      ''
  ),
  requestDurationMs: error?.config?.metadata?.startTime
    ? Date.now() - error.config.metadata.startTime
    : null,
  appVersion: process.env.NEXT_PUBLIC_VERSION ?? '',
  sessionId: getOrCreateSessionId(),
  pageUrl: window.location.href,
  pagePath: window.location.pathname,
  sessionUsername: sessionStorage.getItem('userId') ?? '',
  connectionType: getConnectionType(),
  isOnline: navigator.onLine,
});

const flushOfflineQueue = (): void => {
  const queue = readOfflineQueue();
  if (queue.length === 0) return;
  localStorage.removeItem(OFFLINE_QUEUE_KEY);
  queue.forEach(entry => TrackUbaAudit('errorToastShown', entry));
};

if (typeof window !== 'undefined') {
  // Flush stored offline logs when connectivity is restored
  window.addEventListener('online', () => {
    setTimeout(flushOfflineQueue, 1500);
  });

  // Flush any logs queued during a previous offline session on page load
  if (navigator.onLine) {
    setTimeout(flushOfflineQueue, 2000);
  }
}

/**
 * Call this whenever an error toast is shown to the user.
 * Logs the event via UBA audit immediately if online; queues it in
 * localStorage when offline so it is flushed once connectivity returns.
 *
 * @param toastErrorType  - stable key identifying the error category
 *                          e.g. 'offline' | 'network-timeout' | 'network-error' | 'server-error'
 * @param toastMessage    - the exact message string displayed in the toast
 * @param error           - the original Axios error object for request context
 */
export const logErrorToast = (
  toastErrorType: string,
  toastMessage: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any
): void => {
  if (typeof window === 'undefined') return;

  const entry = buildEntry(toastErrorType, toastMessage, error);

  if (!navigator.onLine) {
    const queue = readOfflineQueue();
    queue.push(entry);
    writeOfflineQueue(queue);
    return;
  }

  TrackUbaAudit('errorToastShown', entry);
};
